import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { TokenService } from './tokenService';

export interface TokenSession {
  id: string;
  tokenHash: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  status: 'active' | 'expired';
}

export interface UserActivity {
  id: string;
  sessionId: string;
  action: 'login' | 'logout' | 'download' | 'page_view' | 'timeout';
  fileName?: string;
  timestamp: Timestamp;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, any>;
}

export interface FileMetadata {
  id: string;
  fileName: string;
  year: number;
  fileSize: number;
  storagePath: string;
  uploadedAt: Timestamp;
  description?: string;
}

export interface DownloadTrack {
  id: string;
  sessionId: string;
  fileId: string;
  fileName: string;
  downloadedAt: Timestamp;
  downloadedBytes?: number;
}

class FirestoreService {
  async createTokenSession(token: string, expiresAtMs: number): Promise<TokenSession> {
    const tokenHash = TokenService.hashToken(token);
    const sessionRef = doc(collection(firestore, 'token_sessions'));

    const status: 'active' | 'expired' = 'active';
    const sessionData = {
      tokenHash,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(new Date(expiresAtMs)),
      status,
    };

    await setDoc(sessionRef, sessionData);

    return {
      id: sessionRef.id,
      tokenHash: sessionData.tokenHash,
      createdAt: sessionData.createdAt,
      expiresAt: sessionData.expiresAt,
      status: sessionData.status,
    };
  }

  async validateTokenSession(tokenHash: string): Promise<TokenSession | null> {
    const q = query(
      collection(firestore, 'token_sessions'),
      where('tokenHash', '==', tokenHash),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    const isValid = TokenService.validateToken(tokenHash, data.expiresAt.toMillis());

    if (!isValid) {
      return null;
    }

    return {
      id: doc.id,
      tokenHash: data.tokenHash,
      createdAt: data.createdAt,
      expiresAt: data.expiresAt,
      status: data.status,
    };
  }

  async logActivity(
    sessionId: string,
    action: UserActivity['action'],
    options?: {
      fileName?: string;
      ipAddress?: string;
      userAgent?: string;
      details?: Record<string, any>;
    }
  ): Promise<void> {
    const activityData = {
      sessionId,
      action,
      timestamp: Timestamp.now(),
      ...(options?.fileName && { fileName: options.fileName }),
      ...(options?.ipAddress && { ipAddress: options.ipAddress }),
      ...(options?.userAgent && { userAgent: options.userAgent }),
      ...(options?.details && { details: options.details }),
    };

    await addDoc(collection(firestore, 'user_activities'), activityData);
  }

  async getFileMetadata(year?: number): Promise<FileMetadata[]> {
    let q;

    if (year) {
      q = query(
        collection(firestore, 'transition'),
        where('year', '==', year),
        orderBy('fileName')
      );
    } else {
      q = query(
        collection(firestore, 'transition'),
        orderBy('year', 'desc'),
        orderBy('fileName')
      );
    }

    const snapshot = await getDocs(q);

    const files = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        uploadedAt: data.uploadedAt,
      } as FileMetadata;
    });

    return files;
  }

  async getFilesByYear(): Promise<Map<number, FileMetadata[]>> {
    const files = await this.getFileMetadata();
    const grouped = new Map<number, FileMetadata[]>();

    for (const file of files) {
      if (!grouped.has(file.year)) {
        grouped.set(file.year, []);
      }
      grouped.get(file.year)!.push(file);
    }

    return grouped;
  }

  async logDownload(
    sessionId: string,
    fileId: string,
    fileName: string,
    downloadedBytes?: number
  ): Promise<void> {
    const downloadData = {
      sessionId,
      fileId,
      fileName,
      downloadedAt: Timestamp.now(),
      ...(downloadedBytes && { downloadedBytes }),
    };

    await addDoc(collection(firestore, 'download_tracking'), downloadData);
  }

  async getSessionActivities(sessionId: string): Promise<UserActivity[]> {
    const q = query(
      collection(firestore, 'user_activities'),
      where('sessionId', '==', sessionId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp,
    } as UserActivity));
  }

  async getSessionDownloads(sessionId: string): Promise<DownloadTrack[]> {
    const q = query(
      collection(firestore, 'download_tracking'),
      where('sessionId', '==', sessionId),
      orderBy('downloadedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      downloadedAt: doc.data().downloadedAt,
    } as DownloadTrack));
  }

  async createFileMetadata(
    fileName: string,
    year: number,
    fileSize: number,
    storagePath: string,
    description?: string
  ): Promise<FileMetadata> {
    const fileData = {
      fileName,
      year,
      fileSize,
      storagePath,
      uploadedAt: Timestamp.now(),
      ...(description && { description }),
    };

    const docRef = await addDoc(collection(firestore, 'transition'), fileData);

    return {
      id: docRef.id,
      ...fileData,
    };
  }

  async markSessionExpired(sessionId: string): Promise<void> {
    const sessionRef = doc(firestore, 'token_sessions', sessionId);
    await setDoc(sessionRef, { status: 'expired' }, { merge: true });
  }
}

export const firestoreService = new FirestoreService();
